import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AfterViewInit, Component, OnDestroy, DoCheck } from '@angular/core';
import Highcharts from 'highcharts';
import { ITableConfig } from 'projects/common/src/app/interfaces/table/table-config';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift } from 'src/app/modules/core/interfaces';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { TimePresentationPipe } from 'src/app/modules/core/pipes/time-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { DashboardFacade } from 'src/app/modules/core/store/dashboard/dashboard.facade';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { IPeriodListPark, IStatusParkChart } from 'src/app/modules/widget/status-park';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { pascalToKebab } from 'projects/common/src/app/services/utils/util.service';
import { point } from 'leaflet';

@Component({
  selector: 'app-status-park',
  templateUrl: './status-park.component.html',
  styleUrls: ['./status-park.component.scss'],
})
export class StatusParkComponent extends DashboardWidgetChartComponent implements DoCheck, OnDestroy {
  public statusParkData: IStatusParkChart;
  public inputDate: any;
  public selectedZone: any;
  public displayMessage: boolean;
  public hasData: boolean;
  public filterDelayOperative: any;
  public tableConfig: ITableConfig;
  public tableData: any;
  public showTable = false;
  public componentName = '';
  public totalSeriesByStatus: any;
  public periodArray: string[];
  public maxMachineSeries: any;

  constructor(
    public dashboardFacade: DashboardFacade,
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private languageService: LanguageService,
    private timePresentation: TimePresentationPipe,
    private formatDatePipe: InputDateFormatPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  public ngDoCheck(): void {
    this.dashboardFacade.dashboardModel$.subscribe((value) => {
      this.showTable = value.isShowTable;
      this.componentName = value.rawComponent?.visibleName;
    });
  }

  /**
   * Init request params
   */
  public initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * Draw chart status Park
   */
  public drawView(chartOptions?: IStatusParkChart): any {
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const user = this.parameterService.parameters.item('user-data').value;
    const machinesTooltip = this.languageService.message('machines');
    const machineTooltip = this.languageService.message('machine');
    const shelvesTooltip =
      machineType.code === 'TRU'
        ? firstLetterUpper(this.languageService.message('transfers'))
        : firstLetterUpper(this.languageService.message('loop'));

    const { zoneName } = this.cacheService.session('selectedZone');
    this.inputDate = String(this.parameterService.parameters.item('date').value);

    let shiftTooltip = '';

    if (shift.name === this.languageService.message('day_detail')) {
      shiftTooltip = this.languageService.message('empty');
    } else {
      shiftTooltip = this.languageService.message('shift_').toUpperCase();
    }

    this.statusParkData = chartOptions;
    this.totalSeriesByStatus = {
      status: this.statusParkData.totalStatusByPeriod,
      periods: this.statusParkData.periodList,
      statusByPorcentagePeriods: this.statusParkData.statusList,
    };

    this.hasData = !!this.statusParkData;
    const buildedSeries = this.buildSeries(this.statusParkData);
    const allZeros = buildedSeries.every((item) => item.data.every((value) => value === 0));

    if (allZeros) {
      this.chartWithoutData$.next(true);
    } else this.chartWithoutData$.next(false);

    const totalMachines = this.filterDelayOperative;

    let plotLines = [];
    let tickInterval = 2;
    let max =
      this.maxMachineSeries?.maxDemora +
      this.maxMachineSeries?.maxOperativo +
      this.maxMachineSeries?.maxReserva +
      this.maxMachineSeries?.maxFueraDeServicio;
    if (this.statusParkData?.stateParkList !== null && this.statusParkData?.stateParkList.length > 0) {
      if (this.statusParkData?.stateParkList[0]?.isActiveLine > 0) {
        plotLines = [
          {
            color: 'black', // Color value
            dashStyle: 'long', // Style of the plot line. Default to solid
            value: this.statusParkData.stateParkList[0].line, // Value of where the line will appear
            width: 2,
            zIndex: 3,
          },
        ];
      }
      if (this.statusParkData?.stateParkList[0]?.isActiveStretch > 0) {
        tickInterval = this.statusParkData.stateParkList[0].stretch;
      }

      if (this.statusParkData?.stateParkList[0]?.line > 0) {
        max = this.statusParkData.stateParkList[0].line + tickInterval;
      }
    }

    const self = this;

    const data = {
      chart: {
        type: 'column',
        backgroundColor: '#F8F8F8',
        height: 450,
      },
      title: {
        text: '', // chartTitle,
        style: {
          fontSize: 12,
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#433E3E',
        },
      },
      subtitle: {
        text: `${this.languageService.message('company')}: ${user.company.name},
              ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
              ${this.languageService.message('date')}: ${this.formatDatePipe.transform(date)},
              ${this.languageService.message('shift_')}: ${shift.name}`,
      },
      credits: {
        enabled: false,
      },
      xAxis: [
        {
          labels: {
            style: {
              color: '#707070',
            },
          },
          categories: this.periodCategoryMapper(this.statusParkData.periodList),
          title: {
            text: this.languageService.message('hours').toUpperCase(),
            yAxis: 20,
            style: {
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
      ],
      yAxis: [
        {
          plotLines,
          tickInterval,
          max,

          labels: {
            style: {
              color: '#707070',
            },
          },
          title: {
            text: this.languageService.message('equipments').toUpperCase(),
            yAxis: 20,
            style: {
              fontSize: 12,
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
        },
        {
          title: {
            text:
              machineType.code === 'TRU'
                ? this.languageService.message('loops').toUpperCase()
                : this.languageService.message('shelves').toUpperCase(),
            yAxis: 20,
            style: {
              color: '#707070',
              fontWeight: '600',
              fontFamily: 'Open Sans',
            },
          },
          labels: {
            format: '{value}',
            style: {
              color: '#707070',
            },
          },
          opposite: true,
        },
      ],
      plotOptions: {
        column: {
          stacking: 'normal',
        },
        series: {
          events: {
            legendItemClick: (e) => {
              e.preventDefault();
            },
          },
        },
      },
      legend: {
        useHTML: true,
        squareSymbol: false,
        reversed: true,
        align: 'center',
        verticalAlign: 'top',
        symbolHeight: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        labelFormatter(): string {
          let figure = '';
          const typeLegend = this.userOptions.type;

          if (typeLegend === 'spline') {
            figure = `<img style="heigth:20px" src="../../../../assets/img/icon/onemine-spline-default.svg" />`;
          } else {
            figure = `
            <svg width="30" height="18" style="float:left">
              <rect width="28" height="12" x="2" y="2"
              style="fill: ${this.color}; stroke-width: 2px !important;
              stroke: ${this.color} !important;"/>
            </svg>`;
          }

          return `
            <div style=" height: 20px; display: flex;" >
            ${figure}
            <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
              color: #433E3E; padding-left:10px">${this.userOptions.name}</span>
            </div>`;
        },
      },
      tooltip: {
        shared: true,
        outside: false,
        backgroundColor: '#333333',
        borderColor: '#33333380',
        borderRadius: 2,
        stickOnContact: false,
        followPointer: false,
        style: {
          opacity: 1,
        },
        useHTML: true,
        formatter(): string {
          const header = `
            <div style="display: flex; flex-direction: column; font-size: 10px; color: #FFFFFF;">
              <div>
                <strong style="font-weight: 700;">${shiftTooltip.toUpperCase()} ${shift && shift.name.toUpperCase()}</strong>
              </div>
          `;

          let totalLabel = '';
          let body = '';

          this.points.forEach((point) => {
            const tooltipCategory = point.series.type === 'spline' ? shelvesTooltip : machineTooltip;
            const total = totalMachines.find((element) => element.category === point.key);
            const isPlural = point.y === 0 || point.y > 1;
            const tooltipProduction = isPlural ? `${tooltipCategory}s ` : `${tooltipCategory}`;
            totalLabel = `<div>
              <span style="font-weight: 400;">TOTAL ${total?.sumMachinesDelayOperative ?? ''} ${machinesTooltip.toUpperCase()}</span>
            </div>`;

            let tooltipBorderSymbol = '';
            if (point.series.type === 'spline') {
              tooltipBorderSymbol = `<img style="height:20px" src="../../../../assets/img/icon/onemine-spline-default.svg" />`;
            } else {
              tooltipBorderSymbol = `<div style="background-color: ${point.color.slice(0, -2)};
                  width: 30px;
                  height: 12px;
                  border: 2px solid ${point.color.slice(0, -2)};
                  margin: 2px 5px 0 0;">
                </div>`;
            }

            body += `<div style="display: flex;">
              ${tooltipBorderSymbol}
              <span style="font-weight: 400;">${point.y} ${tooltipProduction} (${point.series.name}) </span>
              </div>
            `;
          });
          const footer = `<div>${self.languageService.message('between')} <span style="font-weight: 400;">${this.x}  </span></div>`;

          return header + totalLabel + body + footer;
        },
      },
      series: buildedSeries,
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              chart: {
                className: 'small-chart',
              },
            },
          },
        ],
      },
    };

    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
    window.dispatchEvent(new Event('resize'));
  }

  /**
   *  Build 3 series for Status park [progress -> spline, delay -> column orange and operative -> column green]
   *
   * @param statusParkList parameter value
   */
  private buildSeries(statusParkList: IStatusParkChart): any {
    const dynamicSeriesMap = this.periodStatusLoops(this.statusParkData);

    this.filterDelayOperative = Object.keys(dynamicSeriesMap).map((statusName, index) => {
      const sumMachines = dynamicSeriesMap[statusName].data.reduce((acc, val) => acc + val, 0);

      return {
        sumMachines: +(Math.round(sumMachines * 1e2) / 1e2),
        category: this.periodCategoryMapper(this.statusParkData.periodList)[index],
      };
    });

    // Crear series dinámicas basadas en los datos filtrados
    const dynamicSeries = Object.keys(dynamicSeriesMap).map((statusName) => ({
      name: statusName,
      type: 'column',
      yAxis: 0,
      data: dynamicSeriesMap[statusName].data,
      color: `${dynamicSeriesMap[statusName].color}80`,
      borderWidth: 2,
      borderColor: `${dynamicSeriesMap[statusName].color}80`,
      states: {
        hover: {
          color: dynamicSeriesMap[statusName].color,
        },
      },
      zIndex: 2,
    }));

    return [
      {
        name: this.languageService.message('progress'),
        type: 'spline',
        yAxis: 1,
        data: statusParkList.loopsList.map((loop) => loop.productionInPeriod),
        color: '#707070',
        borderWidth: 2,
        zIndex: 3,
      },
      ...dynamicSeries,
    ];
  }

  /**
   *  mapper period for draw x graphic
   *
   * @param periods paramater value
   */
  private periodCategoryMapper(periods: IPeriodListPark[]): string[] {
    this.periodArray = [];

    for (const period of periods) {
      const startTime = this.timePresentation.transform(period.periodStartTime);
      const endTime = this.timePresentation.transform(period.periodEndTime);
      const periodMapped = `${startTime} - ${endTime}`;

      this.periodArray.push(periodMapped);
    }
    return this.periodArray;
  }

  /**
   * status loops for period
   *
   * @param loopsByStatus parameter value
   */
  private periodStatusLoops(loopsByStatus: IStatusParkChart): any {
    const dynamicStatusMap = {}; // Dynamic object to store status data
    const statusList = loopsByStatus.totalStatusByPeriod.reverse();

    // Initialize dynamic object with status data { name and color }
    statusList.forEach((status) => {
      dynamicStatusMap[status.statusName] = {
        data: [],
        color: status.statusFamilyColor,
      };
    });

    if (loopsByStatus.periodList) {
      for (const period of loopsByStatus.periodList) {
        // Filter status by period and push data to dynamic object based on statusName
        statusList.forEach((status) => {
          const result = loopsByStatus.statusList.find((res) => res.periodId === period.periodId && res.statusName === status.statusName);
          dynamicStatusMap[status.statusName].data.push(result ? result.machines : 0);
        });
      }
    }

    return dynamicStatusMap;
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
